<script>
import { tenantService } from '@/helpers/services/tenant.service'
import { sessionHelper, tenantHelper, languageHelper } from '@/utils'

/**
 * Topbar: the brand picker, the agent menu, and the sidebar toggle.
 *
 * ── The brand picker is the multi-tenancy control ────────────────────────
 * one_sales had a company-group dropdown here. Same slot, different meaning:
 * platform staff (us) pick which client they are working in, and that choice is
 * written to localStorage where apiHelper reads it as X-Tenant-Id on every
 * request.
 *
 * A client's own agent never sees the picker, and the header is never consulted
 * for them server-side (App\Support\TenantContext), so there is nothing here
 * they could change to reach another brand. The picker is a convenience for us,
 * not a permission for them.
 *
 * Switching reloads the page deliberately. Every open screen — the inbox queue,
 * an open thread, the FAQ list — is holding data for the old brand, and quietly
 * leaving a JayaKing conversation on screen under a BCB88 header is exactly the
 * confusion that gets an agent to paste the wrong member id into a back office.
 */
export default {
  data() {
    return {
      tenants: [],
      isPlatformStaff: false,
      selected: null,
      user: null,
    }
  },
  computed: {
    /** en / ms / zh only — see language.helper.js for why the other three
     *  locale files are not offered. */
    languages() {
      return languageHelper.list()
    },
    currentLang() {
      return languageHelper.current()
    },
    currentLangName() {
      return languageHelper.currentName()
    },
    selectedName() {
      const t = this.tenants.find((x) => String(x.tenant_id) === String(this.selected))
      if (t) return t.name
      // A brand's NAME is never translated — it is a company. Only the
      // "no brand selected" label is, and which label depends on how wide
      // "no brand" actually is: every brand we host, or just this agent's.
      return this.isPlatformStaff ? this.$t('topbar.allBrands') : this.$t('topbar.myBrands')
    },
    /**
     * Whether this agent gets a picker at all.
     *
     * Staff always: selecting a brand is how they support one. An agent gets
     * one only when they work on SEVERAL — with a single brand there is
     * nothing to choose and a dropdown with one item in it reads as broken.
     */
    canPickBrand() {
      return this.isPlatformStaff || this.tenants.length > 1
    },
  },
  async mounted() {
    // sessionHelper stores the signed-in agent as flat keys, not an object.
    this.user = { name: sessionHelper.getUserName(), email: sessionHelper.getUserEmail() }
    try {
      const res = await tenantService.list()
      this.tenants = res.data || []
      this.isPlatformStaff = !!res.is_platform_staff

      const stored = tenantHelper.current()
      if (this.isPlatformStaff) {
        this.selected = stored == null ? null : String(stored)
      } else {
        /*
         * The SERVER decides, not localStorage. `selected_tenant_id` is the
         * brand it actually scoped this request to: the stored one when the
         * agent works on it, their only brand when they have one, and null
         * when they work on several and have picked none.
         *
         * Reading it back rather than trusting the stored value is what keeps
         * the header honest after somebody's brands change underneath them —
         * a stale id in localStorage then shows a brand they can no longer
         * see, while the data on screen is correctly something else.
         */
        this.selected = res.selected_tenant_id != null ? String(res.selected_tenant_id) : null
        if (this.selected) tenantHelper.set(this.selected)
        else tenantHelper.set(null)
      }
    } catch (e) {
      // A failed tenant list must not blank the whole topbar — the agent can
      // still work in whatever brand was last selected.
    }
  },
  methods: {
    chooseLanguage(code) {
      if (code === this.currentLang) return
      // Reloads — see language.helper.js on why a repaint is not enough.
      languageHelper.set(code)
    },
    choose(tenantId) {
      tenantHelper.set(tenantId)
      // Full reload — see the component docblock.
      window.location.reload()
    },
    toggleMenu() {
      this.$parent.toggleMenu()
    },
    logout() {
      this.$router.push('/logout')
    },
  },
}
</script>

<template>
  <header id="page-topbar">
    <div class="navbar-header">
      <div class="d-flex align-items-center">
        <div class="navbar-brand-box text-center">
          <router-link to="/inbox" class="logo">
            <span class="brand">one<strong>inbox</strong></span>
          </router-link>
        </div>

        <button
          id="vertical-menu-btn"
          type="button"
          class="btn btn-sm px-3 font-size-24 header-item waves-effect"
          @click="toggleMenu"
        >
          <i class="ri-menu-2-line align-middle"></i>
        </button>

        <!-- Brand picker — staff, and anyone working on more than one brand. -->
        <b-dropdown
          v-if="canPickBrand"
          variant="black"
          toggle-class="header-item"
          class="d-inline-block ml-2"
        >
          <template v-slot:button-content>
            <i class="mdi mdi-domain mr-1"></i>
            <span class="d-none d-md-inline">{{ selectedName }}</span>
            <i class="mdi mdi-chevron-down ml-1"></i>
          </template>
          <b-dropdown-item :active="selected === null" @click="choose(null)">
            {{ isPlatformStaff ? $t('topbar.allBrands') : $t('topbar.myBrands') }}
            <div class="small text-muted">
              {{ isPlatformStaff ? $t('topbar.allBrandsHint') : $t('topbar.myBrandsHint') }}
            </div>
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item
            v-for="t in tenants"
            :key="t.tenant_id"
            :active="String(selected) === String(t.tenant_id)"
            @click="choose(t.tenant_id)"
          >
            {{ t.name }}
            <span v-if="!t.is_active" class="badge badge-secondary ml-1">{{ $t('topbar.inactive') }}</span>
          </b-dropdown-item>
        </b-dropdown>

        <!-- An agent on exactly one brand gets a label, not a control. -->
        <span v-else-if="selectedName" class="header-item d-none d-md-inline-block text-muted">
          <i class="mdi mdi-domain mr-1"></i>{{ selectedName }}
        </span>
      </div>

      <div class="d-flex">
        <!-- Language. Each name is written in itself, never translated: this
             control has to be findable by somebody who cannot read the
             language currently on screen. -->
        <b-dropdown right variant="black" toggle-class="header-item" class="d-inline-block">
          <template v-slot:button-content>
            <i class="mdi mdi-translate font-size-18 align-middle mr-1"></i>
            <span class="d-none d-md-inline-block">{{ currentLangName }}</span>
            <i class="mdi mdi-chevron-down ml-1"></i>
          </template>
          <b-dropdown-item
            v-for="l in languages"
            :key="l.code"
            :active="l.code === currentLang"
            @click="chooseLanguage(l.code)"
          >
            {{ l.name }}
          </b-dropdown-item>
        </b-dropdown>

        <b-dropdown right variant="black" toggle-class="header-item" class="d-inline-block">
          <template v-slot:button-content>
            <i class="mdi mdi-account-circle font-size-20 align-middle mr-1"></i>
            <span class="d-none d-xl-inline-block">{{ (user && user.name) || $t('topbar.account') }}</span>
          </template>
          <b-dropdown-item @click="logout">
            <i class="mdi mdi-logout font-size-16 align-middle mr-1"></i> {{ $t('topbar.logout') }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
  </header>
</template>

<style scoped>
.brand { font-size: 18px; letter-spacing: -0.3px; color: #2563eb; }
.brand strong { color: #111827; }
</style>
